import { z } from "zod";

import { FieldMappingSourceType, SalesforceEntityType } from "./salesforce";

/**
 * Contracts of the two mapping-verification endpoints (`POST field-mappings/preview` and
 * `POST sync/test`). They live beside `salesforce.ts` rather than in it because that file is
 * already near the 500-line cap.
 */

// ── Dry run: resolve the mappings against a real prospect, no Salesforce write ──

export const SalesforceFieldMappingPreviewRequestSchema = z.object({
	prospectId: z.number(),
	entityType: z.nativeEnum(SalesforceEntityType),
});

export type SalesforceFieldMappingPreviewRequest = z.infer<
	typeof SalesforceFieldMappingPreviewRequestSchema
>;

export const SalesforceFieldMappingPreviewRowSchema = z.object({
	mappingId: z.number(),
	sourceType: z.nativeEnum(FieldMappingSourceType),
	sourceField: z.string(),
	salesforceField: z.string(),
	salesforceFieldLabel: z.string().nullish(),
	isActive: z.boolean(),
	/**
	 * The value **after** coercion — the Salesforce pass is value-changing (text truncated to
	 * the describe `length`, dates to `YYYY-MM-DD`, multi-picklists re-joined on `;`), so the
	 * raw value would show something the sync never writes.
	 */
	value: z.string().nullable(),
	resolved: z.boolean(),
	isAiMapping: z.boolean(),
	/**
	 * Why the field is dropped: an Opportunity reserved field, a calculated or read-only
	 * target, or a value the coercion pass refuses.
	 */
	skipReason: z.string().nullish(),
});

export type SalesforceFieldMappingPreviewRow = z.infer<
	typeof SalesforceFieldMappingPreviewRowSchema
>;

export const SalesforceFieldMappingPreviewResponseSchema = z.object({
	rows: z.array(SalesforceFieldMappingPreviewRowSchema),
	fallbacks: z.array(z.object({ salesforceField: z.string(), value: z.string() })),
	/**
	 * Fields the sync writes after coercion from ids a dry run does not have —
	 * `AccountId` / `OwnerId` / `RecordTypeId`, per sObject. They are supplied, not missing,
	 * and reporting them as missing required fields would be actively wrong.
	 */
	syncResolvedFields: z.array(z.string()).default([]),
	missingRequired: z.array(z.string()),
});

export type SalesforceFieldMappingPreviewResponse = z.infer<
	typeof SalesforceFieldMappingPreviewResponseSchema
>;

// ── Test sync: the real write, per entity tab ────────────────────────

export const SalesforceTestSyncRequestSchema = z.object({
	prospectId: z.number(),
	entityType: z.nativeEnum(SalesforceEntityType).optional(),
});

export type SalesforceTestSyncRequest = z.infer<typeof SalesforceTestSyncRequestSchema>;

export const SalesforceTestSyncResponseSchema = z.object({
	success: z.boolean(),
	entityType: z.nativeEnum(SalesforceEntityType).optional(),
	/**
	 * OPPORTUNITY and LEAD create a **new** record on every call; CONTACT and ACCOUNT are
	 * adopt-or-update. The modal warns per entity off this flag.
	 */
	createsNewRecord: z.boolean().optional(),
	error: z.string().nullish(),
	/** Salesforce's own code — rejections are returned, not thrown, so the UI can show them. */
	errorCode: z.string().nullish(),
	errorFields: z.array(z.string()).nullish(),
	retryable: z.boolean().optional(),
	salesforceContactId: z.string().nullish(),
	salesforceAccountId: z.string().nullish(),
	salesforceLeadId: z.string().nullish(),
	salesforceOpportunityId: z.string().nullish(),
	recordUrl: z.string().nullish(),
});

export type SalesforceTestSyncResponse = z.infer<typeof SalesforceTestSyncResponseSchema>;
